import PropTypes from 'prop-types';
import FormDialog from '../../../Components/Modal/modal.js';
import './viewResume.scss';
import { CANCEL_TEXT, STAFF_AUGMENTATION } from '../../../Constants/strings.js';
import React, { useEffect, useState, useContext, memo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dropDownIcon from '../../../Assets/images/svg/Path.svg';
import UseAxios from '../../../Api/http/http';
import { updateResume } from '../../../Api/request/resourceRequest';
import { AuthContext } from '../../../Contexts/AuthContext';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'attachment'],
  ],
};

// Custom toolbar component (optional, for more control)
const CustomToolbar = () => (
  <div id="custom-toolbar">
    <div className="custom-toolbar-dropdown-container">
      <select className="ql-header custom-toolbar-select">
        <option value="">Aa</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
      </select>
      <img
        src={dropDownIcon}
        alt="dropdown"
        className="custom-toolbar-dropdown-icon"
      />
    </div>
    <span className="custom-toolbar-divider" />
    <button className="ql-bold" />
    <button className="ql-italic" />
    <button className="ql-underline" />
    <span className="custom-toolbar-divider" />
    <button className="ql-list" value="bullet" />
    <button className="ql-list" value="ordered" />
    <span className="custom-toolbar-divider" />
    <button className="ql-align" value="" />
    <button className="ql-align" value="center" />
    <button className="ql-align" value="right" />
    <button className="ql-align" value="justify" />
    <span className="custom-toolbar-divider" />
    <button className="ql-link" />
  </div>
);

const GenerateResumeDialog = memo(
  ({
    modalOpen,
    setGenerateResumeDialogOpen,
    isEdit,
    htmlResumeContent,
    setHtmlResumeContent,
    selectedRow,
    fetchAndSetResumeUrl,
    setResumeUpdateProgress,
    resumeUpdateProgress,
  }) => {
    // Local state for the editor content
    const [resumeContent, setResumeContent] = useState(htmlResumeContent || '');
    const [isSaving, setIsSaving] = useState(false);
    const { userProfile } = useContext(AuthContext);
    const { snackbarOpen } = useSnackbar();

    // Sync local state with prop when dialog opens or prop changes
    useEffect(() => {
      if (modalOpen) {
        setResumeContent(htmlResumeContent || '');
      }
    }, [modalOpen]);

    // When editor changes, update both local and parent state
    const handleEditorChange = (value) => {
      setResumeContent(value);
      // setconsoleHtmlResumeContent(value);
    };

    // Custom handler for attachment (optional)
    const handleAttachment = () => {
      // Implement file picker logic here
      alert('Attachment clicked!');
    };

    React.useEffect(() => {
      const quill = document
        .querySelector('.ql-editor')
        ?.closest('.ql-container')?.__quill;
      if (quill) {
        quill.getModule('toolbar').addHandler('attachment', handleAttachment);
      }
    }, [modalOpen]);

    const handleClose = () => {
      setGenerateResumeDialogOpen(false);
      setHtmlResumeContent('');
      setResumeContent('');
    };

    const handleSave = async (row) => {
      setResumeUpdateProgress(true);
      setIsSaving(true);
      const payload = {
        staffingId: row?.staffingId,
        employeeId: row?.employeeId,
        lastUpdatedBy: userProfile?.user_name,
        resumeData: resumeContent,
        others: row?.others || '',
        resumeTrackerId: row?.resumeTrackerId,
      };
      const response = await UseAxios(updateResume(payload));
      if (response?.status === 200) {
        snackbarOpen(
          STAFF_AUGMENTATION.REQUEST_RESOURCING.RESUME_UPDATED,
          STAFF_AUGMENTATION.SUCCESS_SEVERITY,
        );
        handleClose();
        setTimeout(() => {
          fetchAndSetResumeUrl(
            row.employeeId,
            row.staffingId,
            row?.resumeTrackerId,
          );
        }, 500);
      } else {
        snackbarOpen(
          STAFF_AUGMENTATION.REQUEST_RESOURCING.RESUME_UPDATE_FAILED,
          STAFF_AUGMENTATION.ERROR_SEVERITY,
        );
      }
      setIsSaving(false); // Enable the Save button again
    };

    return (
      <div>
        <FormDialog
          title={
            <div className="viewResume-header-row">
              <div>
                <span className="viewResume-header-title">
                  {isEdit ? 'Edit Resume' : STAFF_AUGMENTATION.GENERATE_RESUME}
                </span>
              </div>
            </div>
          }
          open={modalOpen}
          button1Action={() => handleClose()}
          button2Action={() => handleSave(selectedRow)}
          dialogPaperCss="view-resume-dialog-paper"
          dialogCss="view-resume-dialog"
          dialogBodycontent={
            resumeUpdateProgress ? (
              <div
                className="loader-wrapper"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  Height: '600px',
                }}
              >
                <img
                  src={PuzzleLoader}
                  alt="Loading..."
                  className="loader-gif"
                  style={{ width: '100px', height: 'auto' }}
                />
              </div>
            ) : (
              <div className="view-resume-dialog-body">
                <div className="viewResume-content-container">
                  <CustomToolbar />
                  <ReactQuill
                    value={resumeContent}
                    onChange={handleEditorChange}
                    theme="snow"
                    modules={{
                      ...quillModules,
                      toolbar: { container: '#custom-toolbar' },
                    }}
                    style={{ minHeight: 200 }}
                  />
                </div>
              </div>
            )
          }
          button1={CANCEL_TEXT}
          button2="Save"
          submitButtonId="generic-email-submit-btn"
          button2Disabled={isSaving}
        />
      </div>
    );
  },
);

GenerateResumeDialog.displayName = 'GenerateResumeDialog';

GenerateResumeDialog.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  setGenerateResumeDialogOpen: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  htmlResumeContent: PropTypes.string,
  setHtmlResumeContent: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  fetchAndSetResumeUrl: PropTypes.func.isRequired,
  setResumeUpdateProgress: PropTypes.func.isRequired,
  resumeUpdateProgress: PropTypes.bool,
};

export default GenerateResumeDialog;
